import { useEffect, useRef, useState } from "react";
import { services } from "@/mocks/services";

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-6");
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      className="py-14 md:py-20 lg:py-24 px-4 md:px-6 lg:px-10 bg-background-100"
    >
      <div
        ref={ref}
        className="max-w-7xl mx-auto opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="mb-8 md:mb-12">
          <span className="inline-block text-xs font-medium text-foreground-500 tracking-widest uppercase border border-foreground-200/60 px-3 py-1 rounded-full mb-4">
            Our Services
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 leading-tight">
              Every Journey
              <br />
              <span className="italic text-primary-500 font-normal">
                A Unique Experience
              </span>
            </h2>
            <p className="text-foreground-600 text-sm max-w-sm md:text-right">
              From airport runs to nightlife, city tours to private events.
              Premium solutions for every need.
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-2 md:gap-1.5 h-auto md:h-[480px] lg:h-[560px]">
          {services.map((service, idx) => (
            <div
              key={service.id}
              onClick={() => setActiveIndex(idx)}
              className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-500 ease-out
                ${idx === activeIndex ? "md:flex-[3]" : "md:flex-1"}
                h-60 md:h-full
              `}
            >
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <p className="text-primary-300 text-xs font-medium uppercase tracking-wider mb-1 md:mb-1.5">
                  {service.subtitle}
                </p>
                <h3 className="font-heading text-base md:text-xl font-semibold text-background-50 mb-1 md:mb-2">
                  {service.title}
                </h3>
                <p
                  className={`text-background-50/80 text-xs md:text-sm leading-relaxed transition-all duration-300 overflow-hidden ${
                    idx === activeIndex
                      ? "max-h-20 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}